/**
 * Header account menu: identity, theme picker and sign-out.
 * Sign-out asks for confirmation — losing a session mid-game costs a coach the
 * next series.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { OverlayDialog } from '../components/overlay-dialog';
import { ROUTES } from '../lib/constants';
import { PERSONA_LABELS, type Persona } from '../lib/personas';

import { THEME_CHOICES, THEME_LABELS, usePreferences } from './preferences-context';
import { useSession } from './session-context';

export function AccountMenu({
  email,
  persona,
  tenantLabel,
}: {
  email: string;
  persona: Persona;
  tenantLabel: string | null;
}): JSX.Element {
  const { signOut } = useSession();
  const { theme, setTheme } = usePreferences();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmingSignOut, setIsConfirmingSignOut] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  async function onConfirmSignOut(): Promise<void> {
    setIsConfirmingSignOut(false);
    setIsOpen(false);
    await signOut();
    navigate(ROUTES.login, { replace: true });
  }

  return (
    <div className="shell__menu" ref={containerRef}>
      <button
        type="button"
        className="shell__action"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span aria-hidden="true">◍</span>
        <span className="shell__action-label">Account</span>
      </button>
      {isOpen ? (
        <div className="shell__menu-panel" role="menu">
          <div className="stack" style={{ gap: 'var(--space-1)', padding: 'var(--space-2)' }}>
            <strong>{email}</strong>
            <span className="muted">{PERSONA_LABELS[persona]}</span>
            {tenantLabel ? <span className="muted">{tenantLabel}</span> : null}
          </div>
          <div className="field" style={{ padding: 'var(--space-2)' }}>
            <label className="field__label" htmlFor="theme-choice">
              Appearance
            </label>
            <select
              id="theme-choice"
              className="field__control"
              value={theme}
              onChange={(event) => setTheme(event.target.value as (typeof THEME_CHOICES)[number])}
            >
              {THEME_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {THEME_LABELS[choice]}
                </option>
              ))}
            </select>
          </div>
          <button type="button" role="menuitem" onClick={() => setIsConfirmingSignOut(true)}>
            Sign out
          </button>
        </div>
      ) : null}
      {isConfirmingSignOut ? (
        <OverlayDialog title="Sign out?" onClose={() => setIsConfirmingSignOut(false)}>
          <div className="overlay__body stack">
            <p>
              Signing out ends this session on this device. Anything already logged is saved; plays
              still queued locally would be lost.
            </p>
            <div className="btn-row">
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void onConfirmSignOut()}
              >
                Sign out
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsConfirmingSignOut(false)}
              >
                Stay signed in
              </button>
            </div>
          </div>
        </OverlayDialog>
      ) : null}
    </div>
  );
}
